import { useState } from "react";
import { Card } from "@/components/ui/card";
import { HelpCircle, ChevronDown } from "lucide-react";

const STEPS = [
  ["Set up shifts first", "Org admins open the \"Shifts & rules\" tab, pick a location and add each shift with a code (e.g. M), a name and start/end times. Add the location's holidays on the holiday calendar and save the rules."],
  ["Open a roster", "Pick the location, optionally a department, and the month. If last month's roster exists you can copy it and only adjust what changed."],
  ["Choose a method", "Auto-balance fills each shift with the number of people it needs. Fixed keeps each person on one shift. Weekly rotation moves everyone through the shifts week by week. Manual lets you fill the grid yourself."],
  ["Pick employees and shifts", "Use the filters to select the team, then tick the shifts this location runs. Enter how many people each shift needs on normal days and on holidays."],
  ["Set week offs", "Pick each person's weekly off day. Every such weekday in the month is off, so 5 Sundays means 5 offs. Leave it empty for Auto to stagger offs."],
  ["Generate, check and send", "Check the chart, grid and rule warnings, and fix any cell by selecting people and assigning a shift. Center managers click \"Submit for approval\"; org admins click \"Approve & publish\". Export to Excel or PDF any time."],
];
const NOTES = [
  "Night and 12-hour shifts: if the end time is earlier than the start (e.g. 8:00 PM to 8:00 AM), the shift ends the next morning. It's shown as \"12h · ends next day\" and counted on the day it starts.",
  "Rest rules count across midnight: after a night shift ending at 8:00 AM, a person isn't put on a shift starting before the minimum rest hours have passed.",
  "Shifts must be between 1 and 16 hours long.",
  "Holidays: on each holiday, only the number of people you set for \"on holidays\" works each shift. People take turns, so whoever worked the last holiday rests on the next one. Everyone else gets H.",
];

export function RosterHelp({ defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="mb-5 border-indigo-200 bg-indigo-50/60" data-testid="roster-help">
      <button className="flex w-full items-center justify-between px-5 py-4 text-left" onClick={() => setOpen(!open)} data-testid="roster-help-toggle">
        <span className="flex items-center gap-2 text-base font-bold text-indigo-950"><HelpCircle className="h-5 w-5" /> How to create a roster</span>
        <ChevronDown className={`h-5 w-5 text-indigo-800 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid gap-6 px-5 pb-5 lg:grid-cols-2">
          <ol className="space-y-3">
            {STEPS.map(([t, d], i) => (
              <li key={t} className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">{i + 1}</span>
                <span><span className="block text-sm font-bold text-slate-900">{t}</span><span className="text-sm text-slate-700">{d}</span></span>
              </li>
            ))}
          </ol>
          <div className="space-y-3 rounded-lg bg-white p-4">
            <p className="text-sm font-bold text-slate-900">Good to know</p>
            {NOTES.map((n) => <p key={n} className="text-sm text-slate-700">• {n}</p>)}
          </div>
        </div>
      )}
    </Card>
  );
}
